import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';

import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ProfileFacadeService } from '@sh/facades/profile-facade.service';
import { AuthService } from '@sh/services/auth/auth.service';
import { ToastService } from '@sh/services/toast/toast.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, RouterModule],
})
export class LoginComponent {
  public isSubmitting = false;
  public loginForm = new FormGroup({
    email: new FormControl('', {
      validators: [Validators.required, Validators.email],
    }),
    password: new FormControl('', { validators: [Validators.required] }),
  });
  profile$ = this.profileFacade.profile$;
  authCredentials$ = this._authService.authCredentials$;

  constructor(
    private _authService: AuthService,
    private _router: Router,
    private _toastService: ToastService,
    private profileFacade: ProfileFacadeService,
  ) {}

  signInWithEmail() {
    if (this.isSubmitting) return;

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    const email = this.loginForm.value.email?.trim().toLowerCase();
    const password = this.loginForm.value.password;
    if (!email || !password) return;

    this.isSubmitting = true;
    return this._authService
      .signInWithEmail(email, password)
      .then(() => {
        const redirectUrl = this._authService.redirectUrl;
        if (redirectUrl) {
          this._router.navigateByUrl(redirectUrl);
          this._authService.redirectUrl = null;
        } else {
          this._router.navigate(['/profile']);
        }
      })
      .catch((error) => {
        let message = 'Unable to sign in. Please try again.';
        if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
          message = 'Email or password is incorrect.';
        } else if (error.code === 'auth/too-many-requests') {
          message = 'Too many attempts. Reset your password or try again later.';
        } else if (error.code === 'auth/network-request-failed') {
          message = 'Network error. Check your connection and try again.';
        }
        this._toastService.showToast({ message }, 'error');
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }

  signOut() {
    this._authService.signOut();
  }

  signInWithGoogle() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    return this._authService
      .signInWithGoogle()
      .then(() => {
        const redirectUrl = this._authService.redirectUrl;
        if (redirectUrl) {
          this._router.navigateByUrl(redirectUrl);
          this._authService.redirectUrl = null;
        } else {
          this._router.navigate(['/profile']);
        }
      })
      .catch((error) => {
        let message = 'Unable to sign in with Google. Please try again.';
        if (error.code === 'auth/popup-closed-by-user') message = 'Google sign-in was cancelled.';
        if (error.code === 'auth/popup-blocked') message = 'Allow pop-ups and try again.';
        this._toastService.showToast({ message }, 'error');
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }

  resetPassword() {
    const email = this.loginForm.value.email?.trim().toLowerCase();
    if (!email || this.loginForm.controls.email.invalid) {
      this.loginForm.controls.email.markAsTouched();
      this._toastService.showToast({ message: 'Enter your email address first.' }, 'error');
      return;
    }

    this._authService.sendPasswordReset(email).then(
      () => this._toastService.showToast({ message: 'Password reset email sent. Check your inbox.' }, 'success'),
      () => this._toastService.showToast({ message: 'We could not send a reset email. Please try again.' }, 'error'),
    );
  }
}
